import type { LetterTask } from './letters'
import { remainingCents, type Payable } from './payments'

/**
 * What Home has to answer, in the order it is asked.
 *
 * spec.md §1879: "Incoming · Needs you · Payment run · 7/30/month totals ·
 * Waiting on others · recent activity; sections hide when empty." Rule 23 is
 * the important half of that sentence — Home shrinks to "alles is in orde"
 * on a quiet day rather than showing a wall of zeroes, and that empty state is
 * a feature. An app that can honestly say nothing needs you is one worth
 * opening.
 *
 * Committed and Estimated are totalled separately and never added together
 * (§10). A forecast that quietly includes guesses is worse than no forecast.
 *
 * Pure, so the arithmetic that decides what the household thinks it owes can
 * be tested without a database.
 */

export interface Window {
  days: number
  label: string
  committedCents: number
  estimatedCents: number
  count: number
}

export interface HomeSummary {
  /** Overdue and needs-attention payments — the things that cannot wait. */
  urgent: Payable[]
  /** Letters with no answer yet. */
  needsYou: LetterTask[]
  /** Letters where the ball is in someone else's court. */
  waitingOn: LetterTask[]
  windows: Window[]
  /** True when there is genuinely nothing to show. */
  clear: boolean
}

const WINDOWS: { days: number; label: string }[] = [
  { days: 7, label: 'Deze week' },
  { days: 30, label: '30 dagen' },
  { days: 90, label: '90 dagen' },
]

export function summariseHome(
  payables: Payable[],
  tasks: LetterTask[],
  today = new Date(),
): HomeSummary {
  const todayIso = localIso(today)
  const open = payables.filter((p) => p.state !== 'paid' && p.state !== 'cancelled')

  const urgent = open
    .filter((p) => !p.isEstimate && (p.dueDate < todayIso || p.state === 'needs_attention'))
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))

  const windows = WINDOWS.map(({ days, label }) => {
    const until = localIso(new Date(today.getTime() + days * 86400000))
    // From today forward. Something already overdue is in `urgent`, where it
    // is a task rather than a forecast — counting it in "deze week" as well
    // would make the week look heavier than it is.
    const inWindow = open.filter((p) => p.dueDate >= todayIso && p.dueDate <= until)
    return {
      days,
      label,
      committedCents: inWindow
        .filter((p) => !p.isEstimate)
        .reduce((sum, p) => sum + remainingCents(p), 0),
      estimatedCents: inWindow
        .filter((p) => p.isEstimate)
        .reduce((sum, p) => sum + p.amountCents, 0),
      count: inWindow.length,
    }
  })

  const live = tasks.filter((t) => !t.doneAt)
  const needsYou = live.filter((t) => !t.waitingOn)
  const waitingOn = live.filter((t) => t.waitingOn)

  return {
    urgent,
    needsYou,
    waitingOn,
    windows,
    clear:
      urgent.length === 0 &&
      needsYou.length === 0 &&
      waitingOn.length === 0 &&
      windows.every((w) => w.count === 0),
  }
}

/** Local date. toISOString() hands back yesterday for anyone up past midnight. */
function localIso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
